'use client';

import { Eye, List } from 'lucide-react';
import { useState } from 'react';
import Chip from '~/components/Chip';
import Skeleton from '~/components/Loading/Skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '~/components/ui/tabs';
import { api, skipBatchOption } from '~/utils/api';
import HomeHighRiskIssues from './HomeHighRiskIssues';
import HomeRecentReviews from './HomeRecentReviews';
import HomeRecentScans from './HomeRecentScans';
import HomeReviewQueue from './HomeReviewQueue';
import { useHomeQueueOverview } from './useHomeQueueOverview';

const pairGridClassName =
  'grid grid-cols-1 gap-3 lg:gap-4 lg:has-[>:nth-child(2)]:grid-cols-2 [&>*]:min-w-0';

const tabChipClassName =
  'h-5 min-w-5 justify-center px-1.5 text-xs leading-none tabular-nums';

const tabTriggerClassName = 'h-9 gap-2 px-3.5 text-sm sm:h-10 sm:px-4';

type HomeWorkTabsProps = {
  canViewReviewQueue: boolean;
  canViewContentScanner: boolean;
  canCreateJob?: boolean;
  onNewJob?: () => void;
};

export default function HomeWorkTabs({
  canViewReviewQueue,
  canViewContentScanner,
  canCreateJob,
  onNewJob,
}: HomeWorkTabsProps) {
  const showTabs = canViewReviewQueue && canViewContentScanner;
  const defaultTab = canViewReviewQueue ? 'queues' : 'scanner';
  const [tab, setTab] = useState(defaultTab);

  const { data: queueOverview, isLoading: isQueueLoading } =
    useHomeQueueOverview(canViewReviewQueue);
  const { data: highRiskResponse, isLoading: isHighRiskLoading } =
    api.scanner.getHighRiskIssues.useQuery(
      { page: 1, limit: 5 },
      { ...skipBatchOption, enabled: canViewContentScanner },
    );

  const openCount = (queueOverview?.queues ?? []).reduce((n, queue) => n + queue.openCount, 0);
  const highRiskCount = highRiskResponse?.totalCount ?? 0;
  const hasHighRisk = highRiskCount > 0;

  const queuePair = (
    <div className={pairGridClassName}>
      <HomeReviewQueue enabled={canViewReviewQueue} showCount={!showTabs} />
      <HomeRecentReviews enabled={canViewReviewQueue} />
    </div>
  );

  const scannerPair = (
    <div className={pairGridClassName}>
      <HomeRecentScans
        enabled={canViewContentScanner}
        canCreateJob={canCreateJob}
        onNewJob={onNewJob}
      />
      <HomeHighRiskIssues enabled={canViewContentScanner} showCount={!showTabs} />
    </div>
  );

  if (!showTabs) {
    return (
      <section aria-label={canViewReviewQueue ? 'Queues and recent tasks' : 'Scans and high-risk issues'}>
        {canViewReviewQueue ? queuePair : scannerPair}
      </section>
    );
  }

  return (
    <section aria-label="Work lists">
      <Tabs value={tab} onValueChange={setTab} className="space-y-3">
        <TabsList aria-label="Work area" className="h-11 gap-1 rounded-xl p-1 sm:h-12">
          <TabsTrigger value="queues" className={tabTriggerClassName}>
            <List className="size-4 shrink-0" strokeWidth={2} aria-hidden />
            Review
            {isQueueLoading ? (
              <Skeleton className="h-5 w-7 rounded-md" />
            ) : (
              <Chip level="neutral" text={String(openCount)} className={tabChipClassName} />
            )}
          </TabsTrigger>
          <TabsTrigger value="scanner" className={tabTriggerClassName}>
            <Eye className="size-4 shrink-0" strokeWidth={2} aria-hidden />
            Content Scanner
            {isHighRiskLoading ? (
              <Skeleton className="h-5 w-7 rounded-md" />
            ) : (
              <Chip
                level={hasHighRisk ? 'alert' : 'neutral'}
                text={String(highRiskCount)}
                className={tabChipClassName}
              />
            )}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="queues" className="mt-0">
          {queuePair}
        </TabsContent>
        <TabsContent value="scanner" className="mt-0">
          {scannerPair}
        </TabsContent>
      </Tabs>
    </section>
  );
}
